<template>
  <section class="test-devices-form-page">
    <div class="personal-device-checkbox">
      <Checkbox v-model="isPersonalDevice" input-id="isPersonalDevice" name="isPersonalDevice" binary />
      <label for="isPersonalDevice" class="ml-2">
        This is
        <strong>my</strong>
        test device
      </label>
    </div>

    <VdForm
      v-if="canWriteTesters"
      ref="formRef"
      :form="form"
      :rules="formRules"
      class="device-form"
      @submit="onAddTestDevice"
    >
      <VdFormField
        v-model="form.name"
        label="Device name"
        placeholder="ex: iPhone 13 Pro"
        prop="name"
        type="text"
        show-word-limit
        max-length="40"
        size="large"
      />

      <VdFormField
        v-model="form.id"
        label="Device ID"
        placeholder="0df565ac-4c9f-42cc-874e-aebd096f2b4d"
        prop="id"
        type="text"
        show-word-limit
        max-length="100"
        size="large"
        :help-text="deviceIdHelpText"
        :error="idfaError"
      />

      <a
        href="https://www.notion.so/voodoo/Device-Management-112a0b481db480738dd4c6430aa90cef"
        target="_blank"
        rel="noopener noreferrer"
        class="device-id-help"
      >
        <VdIcon store="fa" name="arrow-up-right-from-square" />
        How to find your device ID?
      </a>

      <template #actions="{ onSubmit }">
        <section class="add-device-actions">
          <vd-button type="secondary" icon-left="fa:chevron-left" @click="onBack">Back to list</vd-button>
          <vd-button type="primary" :loading="addDeviceLoading" @click="onSubmit">Save</vd-button>
        </section>
      </template>
    </VdForm>
  </section>
</template>

<script setup lang="ts">
import { FormRule, VdForm, VdFormField, VdIcon } from "@voodoo.io/voodoo-ui";
import Checkbox from "primevue/checkbox";
import { computed, reactive, ref } from "vue";
import { RouteLocationRaw, useRouter } from "vue-router";

import {
  TestDevice,
  queryNames,
  useMutationResponse,
  useTestDevicesFormAddTestDeviceMutation,
  useTestDevicesFormTestDevicesQuery,
} from "@/graphql";
import useMessages from "@/hooks/messages";
import usePermissions from "@/hooks/use-permissions";
import links from "@/router/links";

const props = defineProps({
  gameId: {
    type: String,
    required: true,
  },
});

const { canWriteTesters } = usePermissions();
const { alert } = useMessages();
const router = useRouter();
const backLink = computed<RouteLocationRaw>(
  () =>
    (router.options.history.state.back as string) || {
      name: links.games.testDevices.list,
      params: { gameId: props.gameId },
    }
);

const onBack = () => {
  router.push(backLink.value);
};

const formRef = ref();
const isPersonalDevice = ref(true);
const form = reactive({
  id: "",
  name: "",
});

const { result } = useTestDevicesFormTestDevicesQuery(
  { external: !isPersonalDevice.value },
  { fetchPolicy: "network-only" }
);

/**
 * If we don't have the prop externalDevices then filter all the external devices off the response
 */
const testDevices = computed(
  () => result.value?.testDevices.filter((device) => (isPersonalDevice.value ? !device.external : true)) ?? []
);

const formRules: Record<string, FormRule[]> = {
  name: [{ required: true, message: "the device name is required" }],
  id: [
    { required: true, message: "the device ID is required" },
    {
      type: "string",
      validator(_: unknown, idfa: string, callback: (error?: Error) => void) {
        if (testDevices.value.some((device: TestDevice) => device.id === idfa)) {
          return callback(new Error("the device ID is already used"));
        }
        return callback();
      },
    },
  ],
};

const idfaError = ref("");

/**
 * Add a test device
 */
const { mutate: addNewTestDevice, loading: addDeviceLoading } = useTestDevicesFormAddTestDeviceMutation({
  refetchQueries: [queryNames.TestDevicesFormTestDevices],
});

const onAddTestDevice = async () => {
  // TODO - Add the parameters as one GraphQL Input
  const response = await addNewTestDevice({ deviceId: form.id, name: form.name, external: !isPersonalDevice.value });
  const data = useMutationResponse(response, (resp) => resp?.addTestDevice, { shouldNotifyError: false });
  if (data?.success) {
    if (formRef.value) {
      formRef.value.resetFields();
    }
    onBack();
  } else if (data?.errorInternalDetails === "error.userSettings.validation") {
    idfaError.value = "the device ID should be a valid UUID";
  } else if (data?.isConflict) {
    alert(data.errorMessage, { type: "warning" });
  }
};

const deviceIdHelpText = `
    • IDFA for Android<br />
    • IDFV for IOS<br />
    <div style="color: #F56B6B">do not use the editor IDFA found in the Unity Editor</div>
    `;
</script>

<style lang="scss" scoped>
.personal-device-checkbox {
  margin-bottom: 1rem;

  .p-checkbox {
    margin-right: 0.5rem;
  }
}

.add-device-actions {
  display: flex;
  justify-content: space-between;
}

.device-id-help {
  display: block;
  margin-bottom: 2rem;
  text-decoration: underline;

  svg {
    color: var(--vd-base-primary-color);
  }
}
</style>
